import React, { useState } from "react";
import axios from "axios";
import { Download, LogOut, Lock } from "lucide-react";
import { ASSETS } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [key, setKey] = useState("");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const [subsRes, fbRes] = await Promise.all([
        axios.get(`${API}/admin/subscribers`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/admin/feedback`, { headers: { "X-Admin-Key": k } }),
      ]);
      setData(subsRes.data);
      setFeedback(fbRes.data.feedback || []);
      setKey(k);
    } catch {
      setError("Invalid admin key.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => { setKey(""); setData(null); setInput(""); };

  if (!data) {
    return (
      <main data-testid="admin-page" className="min-h-screen flex items-center justify-center px-5 pt-20">
        <div className="w-full max-w-sm bg-ash border border-white/10 rounded-xl p-8">
          <img src={ASSETS.mascot} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-ember mb-5" />
          <h1 className="font-display font-black uppercase tracking-tight text-cream text-2xl flex items-center gap-3">
            <Lock size={18} className="text-ember" /> Boss Mode
          </h1>
          <p className="font-mono text-xs text-cream/65 mt-2">Enter the admin key to see your subscribers.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (input) load(input); }} className="mt-6 space-y-3" data-testid="admin-key-form">
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin key"
              data-testid="admin-key-input"
              className="w-full bg-void border border-white/15 rounded-full px-5 py-3.5 font-mono text-sm text-cream placeholder:text-cream/30 outline-none focus:border-ember transition-colors"
            />
            {error && <p className="font-mono text-xs text-red-400" data-testid="admin-key-error">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-key-submit"
              className="w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-3.5 rounded-full font-medium hover:bg-emberDark transition-colors disabled:opacity-50"
            >
              {loading ? "Checking..." : "Unlock"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  const subs = data.subscribers.filter((s) => filter === "all" || s.source === filter);
  const counts = {
    all: data.subscribers.length,
    newsletter: data.subscribers.filter((s) => s.source === "newsletter").length,
    coffee_club: data.subscribers.filter((s) => s.source === "coffee_club").length,
  };

  return (
    <main data-testid="admin-dashboard" className="pt-28 pb-24 px-5 md:px-10">
      <div className="max-w-[1100px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-3">Boss Mode</p>
            <h1 className="font-display font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl leading-[0.92]">Subscribers</h1>
          </div>
          <div className="flex gap-3">
            <a
              href={`${API}/admin/subscribers/export?key=${encodeURIComponent(key)}`}
              data-testid="admin-export-button"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest bg-ember text-void px-5 py-3 rounded-full font-medium hover:bg-emberDark transition-colors"
            >
              <Download size={14} /> Export CSV
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest border border-white/15 text-cream/75 px-5 py-3 rounded-full hover:border-ember hover:text-ember transition-colors"
            >
              <LogOut size={14} /> Lock
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-10">
          {[
            { id: "all", label: "Total", n: counts.all },
            { id: "newsletter", label: "Newsletter", n: counts.newsletter },
            { id: "coffee_club", label: "Coffee Club", n: counts.coffee_club },
          ].map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              data-testid={`admin-stat-${c.id}`}
              className={`text-left border rounded-lg p-5 transition-colors ${filter === c.id ? "border-ember bg-ash" : "border-white/10 bg-ash/50 hover:border-ember/50"}`}
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-cream/65">{c.label}</p>
              <p className="font-display font-black text-cream text-3xl mt-2">{c.n}</p>
            </button>
          ))}
        </div>

        <div className="mt-8 border border-white/10 rounded-lg overflow-hidden">
          <table className="w-full" data-testid="admin-subscribers-table">
            <thead>
              <tr className="bg-ash font-mono text-[10px] uppercase tracking-widest text-cream/65 text-left">
                <th className="px-5 py-3.5">Email</th>
                <th className="px-5 py-3.5">Source</th>
                <th className="px-5 py-3.5 hidden md:table-cell">Signed up</th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={`${s.email}-${s.source}`} className="border-t border-white/5 font-mono text-xs text-cream/75 hover:bg-ash/60 transition-colors">
                  <td className="px-5 py-3.5">{s.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider ${s.source === "coffee_club" ? "bg-ember text-void" : "border border-white/15 text-cream/75"}`}>
                      {s.source === "coffee_club" ? "Coffee Club" : "Newsletter"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell text-cream/45">{s.created_at ? new Date(s.created_at).toLocaleString() : ""}</td>
                </tr>
              ))}
              {subs.length === 0 && (
                <tr><td colSpan={3} className="px-5 py-10 text-center font-mono text-xs text-cream/55">No subscribers yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-14">
          <h2 className="font-display font-black uppercase tracking-tight text-cream text-2xl">Feedback ({feedback.length})</h2>
          <div className="mt-5 space-y-3" data-testid="admin-feedback-list">
            {feedback.map((f) => (
              <div key={f.id} className="border border-white/10 rounded-lg bg-ash p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-ember font-mono text-sm" data-testid={`feedback-rating-${f.id}`}>{"★".repeat(f.rating)}{"☆".repeat(5 - f.rating)}</span>
                  <span className="font-mono text-xs text-cream/75">{f.name || "Anonymous"}</span>
                  {f.page && <span className="font-mono text-[10px] text-cream/45 border border-white/15 rounded-full px-2 py-0.5">{f.page}</span>}
                  <span className="font-mono text-[10px] text-cream/45 ml-auto">{f.created_at ? new Date(f.created_at).toLocaleString() : ""}</span>
                </div>
                <p className="font-mono text-sm text-cream/80 mt-3 leading-relaxed">{f.message}</p>
              </div>
            ))}
            {feedback.length === 0 && (
              <p className="font-mono text-xs text-cream/55 border border-white/10 rounded-lg bg-ash/50 px-5 py-8 text-center" data-testid="admin-feedback-empty">No feedback yet.</p>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
